import React, { Suspense } from 'react';
import { useHistory, Redirect, Route, Switch, withRouter, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { datadogLogs } from '@datadog/browser-logs';
import { useOktaAuth } from '@okta/okta-react';
import { setUserInfo } from '../../../redux/login/actions';
import { redirectUrlHandler, OktaSecurityHOC, handleLoggedOut } from "../../../helpers/OktaUtility";
import { APP_ROOT_CONTENT_STYLE } from '../../../helpers/constant';
import { setCookie } from '../../../helpers/manageCookies';
import { decrypt, getUserInfo } from '../../../helpers/utilCommon';
import { INTERNAL_APP_PATHNAME, LOGIN_USER_MODEL, SESSION_STORAGE_KEYS, cookieKeysEnum } from '../../../models/common.models';
import { PrivateRoutes, PublicRoute } from '../../../hoc/RouteHOC';
import asyncComponent from '../../../asyncComponent';
import { withFlagFeature } from 'providers/flagFeature';
import FooterComp from '../../organisms/Footer';
import HeaderComp from '../../organisms/Header/Header';
import IdentityDelegationBanner from '../../organisms/IdentityDelegationBanner';
import SafariAutofillMessage from '../../atoms/SafariAutofillMessage';
import CustomLoader from '../../atoms/CustomLoader';
import ScrollToTop from '../../atoms/ScrollToTop';
import ToastMessage from '../../atoms/ToastMessage';
import AventriRegistration from '../aventri';
import MyChildAppComponents from '../myChildAppComponents';
import MyRosterComponents from './RosterChildApp';
import AppLogin from './ChildApp/AppLogin';
import en from 'assets/i18n/en';
import { setMessage } from 'redux/shared/actions';
import { useMediaQuery } from 'react-responsive';
import { setIsMobileBreakpoint } from 'redux/shared/actions';

const WCMSFooter = React.lazy(()=>import("../../organisms/WCMS/WCMSFooter"));
const AlertBanner = React.lazy(()=>import("../../organisms/AlertBanner"));

const MyAccountRoutes = asyncComponent(() => {
    return import('../../../chunks/myAccount.chunk');
});

const DownloadDocumentsRoutes = asyncComponent(() => {
    return import('../../../chunks/downloadDocuments.chunk');
});

const ApplicationsRoutes = asyncComponent(() => {
    return import('../../../chunks/applications.chunk');
});

const StaticPagesRoutes = asyncComponent(() => {
    return import('../../../chunks/staticPages.chunk');
});

const App = (props) => {
    const { isSignOutTrigger, isAppLoading, isMiniAppLoading, showToastMessage, content, uuidNumber, isReactTesting, featureFlag } = props;
    const { authState } = useOktaAuth();
    const location = useLocation();
    const dispatch = useDispatch();
    const { userInfo, memberData } = useSelector(store => store.user);
    const [isOktaInitializing, setIsOktaInitializing] = React.useState(true);
    const [isAlertBanner, setIsAlertBanner] = React.useState(false);
    const [isMemberStillLoginInAnotherTab, setIsMemberStillLoginInAnotherTab] = React.useState('');
    const history = useHistory()
    const isMobile = useMediaQuery({ maxWidth: 767 });
    
    React.useEffect(() => {
        dispatch(setIsMobileBreakpoint(isMobile));
    }, [isMobile, dispatch]);

    React.useEffect(() => {
        window.addEventListener("storage", (e) => {
            if (e.key === 'isMemberStillLoginInAnotherTab' && e.newValue === 'false') {
                handleLoggedOut(dispatch)
            }
        });
    }, [])

    React.useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const alertMessageParam = searchParams.get("alertMessage");
        if (alertMessageParam) {
            const alertCode = searchParams.get("code");
            if (alertCode) {
                const decryptedCode = decrypt(alertCode);
                if (decryptedCode && en.hasOwnProperty(decryptedCode)) {
                    history.replace(location.pathname);
                    dispatch(setMessage(true, decryptedCode));
                }
            }
        }
    }, [location]);

    React.useEffect(() => {
        if (authState?.isAuthenticated && authState?.idToken?.claims) {
            setIsOktaInitializing(true);
            const callbackUrl = redirectUrlHandler();
            if (callbackUrl) {
                history.push(callbackUrl)
                sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_URL)
            }
            setIsAlertBanner(true);
            handleMemberLogin();
        } else {
            setIsOktaInitializing(false);
        }
    }, [authState]);
    
    React.useEffect(() => {
        if (userInfo) {
            datadogLogs.setUser({
                MAEUserID: userInfo.MAEUserID,
                memberAccountNumber: userInfo.memberAccountNumber
            });
        } else {
            datadogLogs.clearUser();
        }
    }, [userInfo]);

    React.useEffect(() => {
        const redirectUrl = window.sessionStorage.getItem(SESSION_STORAGE_KEYS.REDIRECT_CALLBACK_URL);
        if (memberData && redirectUrl) {   
            window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_CALLBACK_URL);
            const pathname = new URL(redirectUrl, window.location.origin).pathname;
            const basePath = "/" + pathname.split("/")[1].toLowerCase();
            if (INTERNAL_APP_PATHNAME.includes(basePath)) {
                history.replace(redirectUrl);
            } else {
                window.location.assign(`${window.location.origin}${redirectUrl}`);
            }
        }
    }, [memberData]);

    const handleMemberLogin = () => {
        const storedUserInfo = getUserInfo();
        if (!storedUserInfo) {
            setCookie(cookieKeysEnum["IS-OKTA-VERIFIED"], true, process.env.REACT_APP_COOKIES_EXPIRATION_TIME);
            let data = {
                userInfo: authState?.idToken?.claims,
                request_mode: LOGIN_USER_MODEL.MEMBER_LOGIN,
            };
            props.handleMemberLogin(data, result => {
                setIsOktaInitializing(false);
            });
        } else {
            dispatch(setUserInfo(storedUserInfo));
            setIsOktaInitializing(false);
        }
    }

    return (
        <>
            {isMiniAppLoading ? <CustomLoader testId='mini-spinner' /> : null}
            {isAppLoading || isSignOutTrigger || isOktaInitializing  ? <CustomLoader testId='app-spinner' /> : null}
            {showToastMessage ? (<div className="topAlert"><ToastMessage content={content} uuidNumber={uuidNumber} /></div>) : null}
            <ScrollToTop />
            <SafariAutofillMessage />
            {isAlertBanner && (
                <Suspense>
                    <AlertBanner />
                </Suspense>
            )}
            {!isReactTesting && <HeaderComp/>}
            <IdentityDelegationBanner />
            <section style={APP_ROOT_CONTENT_STYLE}>
                {!(isSignOutTrigger || isOktaInitializing) &&
                    <Switch>
                        <PublicRoute path='/application' component={ApplicationsRoutes} />
                        <PublicRoute path='/Msgs' component={StaticPagesRoutes} />
                        <PublicRoute path='/aventri-registration' component={AventriRegistration} />
                        <PublicRoute path='/download' component={DownloadDocumentsRoutes} />
                        <PrivateRoutes path="/MyASTM" component={MyAccountRoutes} isMemberStillLoginInAnotherTab={isMemberStillLoginInAnotherTab} />
                        <PrivateRoutes path="/ballot" component={MyChildAppComponents} isMemberStillLoginInAnotherTab={isMemberStillLoginInAnotherTab} />
                        <PrivateRoutes path="/wi" component={MyChildAppComponents} isMemberStillLoginInAnotherTab={isMemberStillLoginInAnotherTab} />
                        <PrivateRoutes path="/standard" component={MyChildAppComponents} isMemberStillLoginInAnotherTab={isMemberStillLoginInAnotherTab} />
                        <PrivateRoutes path="/meeting" component={MyChildAppComponents} isMemberStillLoginInAnotherTab={isMemberStillLoginInAnotherTab} />
                        <PrivateRoutes path="/RMLogin" component={MyRosterComponents} isMemberStillLoginInAnotherTab={isMemberStillLoginInAnotherTab} />
                        <PrivateRoutes path="/AppLogin" component={AppLogin} isMemberStillLoginInAnotherTab={isMemberStillLoginInAnotherTab} />
                        <Redirect from='/login' to="/MyASTM" />
                        <Redirect excat from='/' to="/MyASTM" />
                        <Route path='*' component={(props) => {
                            const { pathname } = props.location;
                            if (pathname !== '/auth') {
                                window.open(process.env.REACT_APP_PUB_404_URL, "_self");
                            }
                            return false;
                        }} />
                    </Switch>
                }
            </section>
            {!featureFlag?.WcmsNavIntegration ? (
                <FooterComp />
            ) : (
                <Suspense>
                    <WCMSFooter />
                </Suspense>
                
            )}
        </>
    );
}

const RouterApp = (props) => {
    return (
        <OktaSecurityHOC>
            <App {...props} />
        </OktaSecurityHOC>
    );
}

export default withFlagFeature(withRouter(RouterApp));